import {
  PREVIEW_SCREENS,
  PreviewScreenType,
  QuestionData,
} from 'src/types/formQuestionnaire';
import { formatFileSize } from 'src/utils';
import { PreviewAction, QuestionAnswer } from '../FormPreview.reducer';
import {
  resolveNextStep,
  resolveReachableQuestions,
} from './resolveBranchingPath';

const hasString = (s?: string) => Boolean(s?.trim());
const hasGridContent = (row: Record<string, string>): boolean =>
  Object.values(row).some(Boolean);

const isAnswered = (a: QuestionAnswer, question?: QuestionData): boolean => {
  let checkboxAnswered = (a.checkboxOptions?.length ?? 0) > 0;

  if (a.radioOption && question) {
    const selectedOption = question.options?.find(
      (opt) => opt.id === a.radioOption,
    );
    if (selectedOption?.isOtherOption) {
      return hasString(a.otherOptionText);
    }
  }

  if (a.checkboxOptions?.length && question) {
    const selectedOptions =
      question.options?.filter((opt) => a.checkboxOptions?.includes(opt.id)) ||
      [];
    const selectedNonOther = selectedOptions.some((opt) => !opt.isOtherOption);
    const selectedOther = selectedOptions.some((opt) => opt.isOtherOption);

    if (selectedNonOther) {
      checkboxAnswered = true;
    } else if (selectedOther) {
      checkboxAnswered = hasString(a.otherOptionText);
    } else {
      checkboxAnswered = false;
    }
  }

  return (
    hasString(a.shortText) ||
    hasString(a.longText) ||
    hasString(a.numeric) ||
    hasString(a.date) ||
    hasString(a.time) ||
    Boolean(a.radioOption) ||
    Boolean(a.singleDropdownOption) ||
    checkboxAnswered ||
    (a.multiDropdownOptions?.length ?? 0) > 0 ||
    (a.uploadedFiles?.length ?? 0) > 0 ||
    (a.dataGridRows?.some(hasGridContent) ?? false)
  );
};

export const getAnsweredCount = (
  answers: Record<string, QuestionAnswer>,
  questions: QuestionData[],
): number => {
  return questions.filter((question) => {
    const answer = answers[question.id];
    return answer != null && isAnswered(answer, question);
  }).length;
};

export { formatFileSize };

const SUPPORTED_EXTENSIONS = [
  '.png',
  '.apng',
  '.jpg',
  '.jpeg',
  '.pjpeg',
  '.svg',
  '.gif',
  '.tiff',
  '.tif',
  '.bmp',
  '.psd',
  '.avif',
  '.txt',
  '.csv',
  '.html',
  '.xml',
  '.mp4',
  '.mov',
  '.mp1s',
  '.asf',
  '.ogg',
  '.wav',
  '.wma',
  '.ps',
  '.docx',
  '.doc',
  '.pptx',
  '.ppt',
  '.xlsx',
  '.xls',
  '.dcm',
  '.pdf',
];

export const SUPPORTED_FILE_TYPES = SUPPORTED_EXTENSIONS.join(',');

export const MAX_PREVIEW_FILE_SIZE_MB = 25;
export const MAX_PREVIEW_FILE_SIZE_BYTES =
  MAX_PREVIEW_FILE_SIZE_MB * 1024 * 1024;

export const MAX_PREVIEW_FORM_SIZE_MB = 100;
export const MAX_PREVIEW_FORM_SIZE_BYTES =
  MAX_PREVIEW_FORM_SIZE_MB * 1024 * 1024;

const validateFiles = (
  files: FileList,
): { valid: File[]; invalidType: string[]; oversized: string[] } => {
  const valid: File[] = [];
  const invalidType: string[] = [];
  const oversized: string[] = [];
  const supportedExtensionsSet = new Set(
    SUPPORTED_EXTENSIONS.map((ext) => ext.toLowerCase()),
  );

  Array.from(files).forEach((file) => {
    const extension = file.name.includes('.')
      ? `.${file.name.split('.').pop()?.toLowerCase()}`
      : '';

    if (!extension || !supportedExtensionsSet.has(extension)) {
      invalidType.push(file.name);
    } else if (file.size > MAX_PREVIEW_FILE_SIZE_BYTES) {
      oversized.push(file.name);
    } else {
      valid.push(file);
    }
  });

  return { valid, invalidType, oversized };
};

const getTotalUploadedBytes = (
  answers: Record<string, QuestionAnswer>,
): number =>
  Object.values(answers).reduce(
    (total, answer) =>
      total +
      (answer.uploadedFiles?.reduce((sum, file) => sum + file.size, 0) ?? 0),
    0,
  );

const splitByFormSizeLimit = (
  files: File[],
  existingTotalBytes: number,
): { withinLimit: File[]; exceedsLimit: string[] } => {
  const withinLimit: File[] = [];
  const exceedsLimit: string[] = [];
  let runningTotal = existingTotalBytes;

  files.forEach((file) => {
    if (runningTotal + file.size > MAX_PREVIEW_FORM_SIZE_BYTES) {
      exceedsLimit.push(file.name);
    } else {
      runningTotal += file.size;
      withinLimit.push(file);
    }
  });

  return { withinLimit, exceedsLimit };
};

const mapFilesToPayload = (files: File[]) =>
  files.map((file) => ({
    id: `${Date.now()}-${Math.random()}`,
    name: file.name,
    size: file.size,
  }));

const simulateUploadProgress = (
  dispatch: React.Dispatch<PreviewAction>,
  questionId: string,
  fileIds: string[],
  activeIntervals: Set<ReturnType<typeof setInterval>>,
) => {
  fileIds.forEach((fileId) => {
    let progress = 0;
    const interval = setInterval(() => {
      progress += Math.floor(Math.random() * 20) + 10;
      if (progress >= 100) {
        progress = 100;
        clearInterval(interval);
        activeIntervals.delete(interval);
      }
      dispatch({
        type: 'UPDATE_FILE_PROGRESS',
        questionId,
        fileId,
        progress,
      });
    }, 200);
    activeIntervals.add(interval);
  });
};

export const handleDragEvent = (event: React.DragEvent<HTMLDivElement>) => {
  event.preventDefault();
  event.stopPropagation();
};

export const createPreviewHandlers = (
  dispatch: React.Dispatch<PreviewAction>,
  currentQuestionId: string | null,
  screen: PreviewScreenType,
  currentQuestionIndex: number,
  isLastQuestion: boolean,
  activeIntervals?: Set<ReturnType<typeof setInterval>>,
  onFileValidationError?: (invalidFiles: string[]) => void,
  onFileSizeError?: (oversizedFiles: string[]) => void,
  onFormSizeError?: (files: string[]) => void,
  branchingContext?: {
    questions: QuestionData[];
    answers: Record<string, QuestionAnswer>;
  },
) => {
  const intervals =
    activeIntervals ?? new Set<ReturnType<typeof setInterval>>();

  const handleNext = () => {
    if (screen === PREVIEW_SCREENS.FORM_INFO) {
      dispatch({ type: 'SET_SCREEN', payload: PREVIEW_SCREENS.QUESTIONS });
      dispatch({ type: 'SET_QUESTION_INDEX', payload: 0 });
      return;
    }

    if (isLastQuestion) return;

    const currentQuestion = branchingContext?.questions.find(
      (q) => q.id === currentQuestionId,
    );
    const nextStep = currentQuestion
      ? resolveNextStep(
          currentQuestion,
          currentQuestionId
            ? branchingContext?.answers[currentQuestionId]
            : undefined,
        )
      : { type: 'next' as const };

    if (nextStep.type === 'submit') return;

    if (nextStep.type === 'question') {
      const targetIndex = branchingContext?.questions.findIndex(
        (q) => q.id === nextStep.questionId,
      );
      dispatch({
        type: 'SET_QUESTION_INDEX',
        payload:
          targetIndex != null && targetIndex >= 0
            ? targetIndex
            : currentQuestionIndex + 1,
      });
      return;
    }

    dispatch({
      type: 'SET_QUESTION_INDEX',
      payload: currentQuestionIndex + 1,
    });
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!currentQuestionId) return;
    const files = event.target.files;
    if (files) {
      const { valid, invalidType, oversized } = validateFiles(files);
      const { withinLimit, exceedsLimit } = splitByFormSizeLimit(
        valid,
        getTotalUploadedBytes(branchingContext?.answers ?? {}),
      );

      if (withinLimit.length > 0) {
        const payload = mapFilesToPayload(withinLimit);
        dispatch({
          type: 'ADD_FILES',
          questionId: currentQuestionId,
          payload,
        });
        simulateUploadProgress(
          dispatch,
          currentQuestionId,
          payload.map((f) => f.id),
          intervals,
        );
      }

      if (invalidType.length > 0 && onFileValidationError) {
        onFileValidationError(invalidType);
      }

      if (oversized.length > 0 && onFileSizeError) {
        onFileSizeError(oversized);
      }

      if (exceedsLimit.length > 0 && onFormSizeError) {
        onFormSizeError(exceedsLimit);
      }
    }
    event.target.value = '';
  };

  const handleDeleteFile = (fileId: string) => {
    if (!currentQuestionId) return;
    dispatch({
      type: 'DELETE_FILE',
      questionId: currentQuestionId,
      payload: fileId,
    });
  };

  const handleFileDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (!currentQuestionId) return;
    const files = event.dataTransfer.files;
    if (files && files.length > 0) {
      const { valid, invalidType, oversized } = validateFiles(files);
      const { withinLimit, exceedsLimit } = splitByFormSizeLimit(
        valid,
        getTotalUploadedBytes(branchingContext?.answers ?? {}),
      );

      if (withinLimit.length > 0) {
        const payload = mapFilesToPayload(withinLimit);
        dispatch({
          type: 'ADD_FILES',
          questionId: currentQuestionId,
          payload,
        });
        simulateUploadProgress(
          dispatch,
          currentQuestionId,
          payload.map((f) => f.id),
          intervals,
        );
      }

      if (invalidType.length > 0 && onFileValidationError) {
        onFileValidationError(invalidType);
      }

      if (oversized.length > 0 && onFileSizeError) {
        onFileSizeError(oversized);
      }

      if (exceedsLimit.length > 0 && onFormSizeError) {
        onFormSizeError(exceedsLimit);
      }
    }
  };

  const handleBack = () => {
    if (screen !== PREVIEW_SCREENS.QUESTIONS || currentQuestionIndex <= 0) {
      return;
    }

    if (branchingContext) {
      const reachableQuestions = resolveReachableQuestions(
        branchingContext.questions,
        branchingContext.answers,
      );
      const reachableIndex = reachableQuestions.findIndex(
        (question) => question.id === currentQuestionId,
      );
      const previousQuestion = reachableQuestions[reachableIndex - 1];
      if (previousQuestion) {
        const previousIndex = branchingContext.questions.findIndex(
          (question) => question.id === previousQuestion.id,
        );
        dispatch({ type: 'SET_QUESTION_INDEX', payload: previousIndex });
        return;
      }
    }

    dispatch({
      type: 'SET_QUESTION_INDEX',
      payload: currentQuestionIndex - 1,
    });
  };

  const cleanup = () => {
    intervals.forEach((id) => clearInterval(id));
    intervals.clear();
  };

  return {
    handleNext,
    handleBack,
    handleFileSelect,
    handleDeleteFile,
    handleFileDrop,
    cleanup,
  };
};
